/**
 * Recherche / remplacement de texte dans le document, à la manière de
 * Ctrl+F / Ctrl+H dans Word.
 *
 * Le texte d'un élément est lu nœud texte par nœud texte : une occurrence peut
 * chevaucher plusieurs balises inline (gras, italique…) mais jamais deux blocs
 * (paragraphes, éléments de liste, cellules, sauts de ligne).
 * Le remplacement ne réécrit que les nœuds texte : balises et mise en forme
 * sont conservées, le texte de remplacement prend le style du début de l'occurrence.
 *
 * La comparaison ignore la différence entre espace et espace insécable
 * (le HTML des rubriques en contient beaucoup) et entre apostrophes droites et typographiques.
 */

export const FIND_HIGHLIGHT = 'bluenote-find';
export const FIND_CURRENT_HIGHLIGHT = 'bluenote-find-current';
export const MAX_FIND_MATCHES = 5000;

// Inséré entre deux blocs : aucune occurrence ne peut le contenir
const BLOCK_SEPARATOR = '\u0000';

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'BR', 'CAPTION', 'DD', 'DIV', 'DL', 'DT',
  'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'HEADER', 'HR', 'IMG', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION', 'TABLE', 'TBODY',
  'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'UL'
]);

const SKIPPED_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION',
  'BUTTON', 'IFRAME', 'SVG'
]);

const QUOTE_FOLDS = {
  '\u2018': "'", '\u2019': "'", '\u201A': "'", '\u201B': "'", '\u2032': "'", '\u02BC': "'",
  '\u201C': '"', '\u201D': '"', '\u201E': '"', '\u2033': '"'
};

// Caractères invisibles insérés par TinyMCE autour du curseur : jamais assimilés à une espace
const ZERO_WIDTH_CHARS = new Set(['\u200B', '\uFEFF']);

const WORD_CHAR = /[\p{L}\p{N}\p{M}_]/u;

const isWordChar = (ch) => Boolean(ch) && WORD_CHAR.test(ch);

// Éléments techniques de TinyMCE (signets, curseurs) à ne pas lire comme du texte
const isEditorInternal = (element) => (
  element.getAttribute('data-mce-bogus') === 'all' ||
  element.getAttribute('data-mce-type') === 'bookmark'
);

/**
 * Nettoie une saisie (recherche ou remplacement) : supprime les caractères de contrôle
 * et remplace tabulations / retours à la ligne par une espace.
 */
export const sanitizeFindInput = (value) => Array.from(String(value ?? '')).map((ch) => {
  const code = ch.charCodeAt(0);
  if (code === 9 || code === 10 || code === 13) return ' ';
  return code < 32 || code === 127 ? '' : ch;
}).join('');

// Pliage caractère par caractère : la longueur est conservée pour garder la correspondance des positions
const foldText = (text, matchCase) => {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    let ch = text[i];
    if (ch !== BLOCK_SEPARATOR && !ZERO_WIDTH_CHARS.has(ch)) {
      if (/\s/.test(ch)) {
        ch = ' ';
      } else if (QUOTE_FOLDS[ch]) {
        ch = QUOTE_FOLDS[ch];
      } else if (!matchCase) {
        const lower = ch.toLowerCase();
        if (lower.length === 1) ch = lower;
      }
    }
    out += ch;
  }
  return out;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildPattern = (query, matchCase) => {
  const folded = foldText(sanitizeFindInput(query), matchCase);
  if (!folded) return null;
  // Une espace de la recherche couvre une suite d'espaces affichée comme une seule
  return escapeRegExp(folded).replace(/ +/g, ' +');
};

/**
 * Trouve les occurrences de `query` dans `text`.
 * @returns {{ start: number, end: number }[]} positions dans `text`, sans chevauchement
 */
export const findMatches = (text, query, { matchCase = false, wholeWord = false } = {}, limit = Infinity) => {
  const pattern = buildPattern(query, matchCase);
  if (!pattern || !text) return [];

  const haystack = foldText(text, matchCase);
  const regex = new RegExp(pattern, 'g');
  const matches = [];
  let result = regex.exec(haystack);

  while (result !== null && matches.length < limit) {
    const start = result.index;
    const end = start + result[0].length;

    if (end === start) {
      regex.lastIndex = start + 1;
    } else if (
      wholeWord && (
        (isWordChar(haystack[start]) && isWordChar(haystack[start - 1])) ||
        (isWordChar(haystack[end - 1]) && isWordChar(haystack[end]))
      )
    ) {
      regex.lastIndex = start + 1;
    } else {
      matches.push({ start, end });
    }
    result = regex.exec(haystack);
  }

  return matches;
};

/**
 * Lit le texte d'un élément et la position de chaque nœud texte dans ce texte.
 * @returns {{ text: string, segments: { node: Text, start: number, length: number }[] }}
 */
export const collectTextSegments = (root) => {
  let text = '';
  const segments = [];

  const visit = (parent) => {
    for (let node = parent.firstChild; node; node = node.nextSibling) {
      if (node.nodeType === TEXT_NODE) {
        const length = node.data.length;
        if (length) {
          segments.push({ node, start: text.length, length });
          text += node.data;
        }
      } else if (node.nodeType === ELEMENT_NODE) {
        const tag = node.nodeName.toUpperCase();
        if (SKIPPED_TAGS.has(tag) || isEditorInternal(node)) continue;
        const isBlock = BLOCK_TAGS.has(tag);
        if (isBlock) text += BLOCK_SEPARATOR;
        visit(node);
        if (isBlock) text += BLOCK_SEPARATOR;
      }
    }
  };

  if (root) visit(root);
  return { text, segments };
};

// Dernier segment qui commence avant (ou à) la position donnée
const findSegmentIndex = (segments, position) => {
  let low = 0;
  let high = segments.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (segments[mid].start <= position) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found;
};

// Morceaux de nœuds texte couverts par une occurrence, dans l'ordre du document
const getMatchParts = (segments, { start, end }) => {
  const parts = [];
  for (let i = Math.max(0, findSegmentIndex(segments, start)); i < segments.length; i += 1) {
    const segment = segments[i];
    if (segment.start >= end) break;
    const from = Math.max(start, segment.start) - segment.start;
    const to = Math.min(end, segment.start + segment.length) - segment.start;
    if (to > from) parts.push({ node: segment.node, from, to });
  }
  return parts;
};

/**
 * Crée un Range DOM couvrant une occurrence (dans le document qui contient les nœuds).
 */
export const createMatchRange = (segments, match) => {
  const parts = getMatchParts(segments, match);
  if (!parts.length) return null;
  const first = parts[0];
  const last = parts[parts.length - 1];
  const range = first.node.ownerDocument.createRange();
  range.setStart(first.node, first.from);
  range.setEnd(last.node, last.to);
  return range;
};

const applyReplacements = (segments, matches, replacement) => {
  // De la fin vers le début : les positions des occurrences précédentes restent valables
  for (let m = matches.length - 1; m >= 0; m -= 1) {
    getMatchParts(segments, matches[m]).forEach((part, index) => {
      const data = part.node.data;
      part.node.data = data.slice(0, part.from) + (index === 0 ? replacement : '') + data.slice(part.to);
    });
  }
};

const selectMatches = (matches, { index = null, expectedCount = null }) => {
  // Le contenu ne correspond plus à ce qui était affiché lors de la recherche
  if (expectedCount !== null && matches.length !== expectedCount) return null;
  if (index === null) return matches;
  return matches[index] ? [matches[index]] : null;
};

/**
 * Remplace dans les nœuds texte d'un élément (DOM vivant, ex. corps d'un éditeur TinyMCE).
 * `target.index` limite le remplacement à une occurrence ; `target.expectedCount`
 * annule l'opération si le nombre d'occurrences a changé depuis la recherche.
 * @returns {{ replaced: number, stale: boolean }}
 */
export const replaceInElement = (root, query, options, replacement, target = {}) => {
  const { text, segments } = collectTextSegments(root);
  const selected = selectMatches(findMatches(text, query, options), target);
  if (!selected) return { replaced: 0, stale: true };
  applyReplacements(segments, selected, sanitizeFindInput(replacement));
  return { replaced: selected.length, stale: false };
};

/**
 * Remplace dans une chaîne HTML (analysée dans un document inerte : ni script ni image chargés).
 * @returns {{ html: string, replaced: number, stale: boolean }}
 */
export const replaceInHtml = (html, query, options, replacement, target = {}) => {
  const doc = document.implementation.createHTMLDocument('');
  const root = doc.createElement('div');
  root.innerHTML = html || '';
  const result = replaceInElement(root, query, options, replacement, target);
  return { ...result, html: result.replaced ? root.innerHTML : html };
};

/**
 * Remplace dans un texte brut (ex. titre de rubrique).
 * @returns {{ text: string, replaced: number, stale: boolean }}
 */
export const replaceInText = (value, query, options, replacement, target = {}) => {
  const text = value || '';
  const selected = selectMatches(findMatches(text, query, options), target);
  if (!selected) return { text, replaced: 0, stale: true };
  const cleanReplacement = sanitizeFindInput(replacement);
  let out = text;
  for (let m = selected.length - 1; m >= 0; m -= 1) {
    out = out.slice(0, selected[m].start) + cleanReplacement + out.slice(selected[m].end);
  }
  return { text: out, replaced: selected.length, stale: false };
};

/**
 * Règles CSS des surlignages (document principal et iframes TinyMCE).
 */
export const buildFindHighlightCss = (isDark) => {
  const colors = isDark
    ? { match: 'rgba(255, 213, 79, 0.38)', current: '#ffb74d', currentText: '#1a1a1a' }
    : { match: 'rgba(255, 235, 59, 0.65)', current: '#ff9632', currentText: '#1a1a1a' };
  return `
    ::highlight(${FIND_HIGHLIGHT}) { background-color: ${colors.match}; }
    ::highlight(${FIND_CURRENT_HIGHLIGHT}) { background-color: ${colors.current}; color: ${colors.currentText}; }
  `;
};
